import React, { useContext, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isMobile, isTablet } from 'react-device-detect';
import { Tooltip } from 'antd';

import { ReportsContext } from 'contexts/reporting';
import { SessionContext } from 'contexts/session';

import Button from 'components/button/Button';
import { Spinner } from 'components/loadings';
import { AdvanceSubtitle, Filter, Filters, TeamsSelector } from './ReportingFiltersComponents';
import UsersSelectMultiple from './shared/UserSelectMultiple/UserSelectMultiple';
import { filters } from './ReportingFiltersConfig';
import ScheduleReportModal from './ScheduleReportModal';

import {
  ACCOUNT,
  COURSES,
  TEAM_ACTIVITY,
  USER_ACTIVITY,
  coursesColumnName,
  skillDiveColumnName,
  skillSonarColumnName,
  teamColumnName,
  usersColumnName,
} from 'constants/reporting';

import FeatherIcon from 'feather-icons-react';
import DateRangeFilter from './shared/DateRangeFilter';

const ReportingFilters = ({
  filtersType,
  hasSkillDive,
  sonarEnabled,
}: {
  filtersType: string;
  hasSkillDive: boolean;
  sonarEnabled: boolean;
}): JSX.Element => {
  const { useAutomatedReports, userProgressReportsCheck } = useFlags();
  const {
    generateNewReport,
    fetching,
    fetchingFilters,
    activeFilters,
    setActiveFilters,
    getReportsFilteringPreferences,
  } = useContext(ReportsContext);
  const [dateFilter, setDateFilter] = useState(null);
  const [usersSelected, setUsersSelected] = useState([]);
  const [teamsSelected, setTeamsSelected] = useState([]);
  const { isTeamManager } = useContext(SessionContext);

  useEffect(() => {
    getReportsFilteringPreferences(filtersType);
  }, [filtersType]);

  const onGenerateReportClick = async () => {
    const params = {
      type: filtersType,
      datesFilter: {
        from: dateFilter.from,
        to: dateFilter.to,
      },
      activeFilters: activeFilters,
    };
    if (filtersType === USER_ACTIVITY) {
      params['uaa_ids'] = usersSelected;
    }
    if (filtersType === TEAM_ACTIVITY) {
      params['team_ids'] = teamsSelected.map((t) => t.team_id);
    }
    generateNewReport(params);
  };

  const handleUsersSelectionChange = (selectedUsers) => {
    setUsersSelected(selectedUsers.map((user) => user.id));
  };

  const handleTeamsSelectionChange = (selectedTeams) => {
    const teams = [];
    if (selectedTeams?.length > 0) {
      selectedTeams.forEach((team) => {
        teams.push({
          team,
          team_id: team.id,
          name: team.name,
          is_manager: !!team.is_manager,
        });
      });
    }
    setTeamsSelected(teams);
  };

  const [showScheduleReportModal, setShowScheduleReportModal] = useState(false);

  useEffect(() => {
    const handleGenerate = () => onGenerateReportClick();
    const handleSchedule = () => setShowScheduleReportModal(true);
    window.addEventListener('reporting:generate', handleGenerate);
    window.addEventListener('reporting:schedule', handleSchedule);
    return () => {
      window.removeEventListener('reporting:generate', handleGenerate);
      window.removeEventListener('reporting:schedule', handleSchedule);
    };
  }, [dateFilter, activeFilters, usersSelected, teamsSelected]);

  const ActionButtons = () => {
    const selectedCount = activeFilters?.length || 0;
    const buttonsDisabled = fetching || selectedCount === 0 || !dateFilter;
    const selectedLabel =
      selectedCount === 1 ? 'Selected metric: 1' : `Selected metrics: ${selectedCount}`;

    return (
      <Filters.Buttons>
        <Filters.MobileSummary>
          <span>{selectedLabel}</span>
        </Filters.MobileSummary>
        <Filters.MobileButtonsRow>
          {useAutomatedReports && (
            <Button
              $secondary
              size={isMobile ? undefined : 'small'}
              style={isMobile ? { backgroundColor: 'white' } : undefined}
              disabled={buttonsDisabled}
              onClick={() => setShowScheduleReportModal(true)}
            >
              Schedule report
            </Button>
          )}
          <Button
            data-testid="filter-button-test-id"
            $primary
            size={isMobile ? undefined : 'small'}
            disabled={buttonsDisabled}
            onClick={() => onGenerateReportClick()}
          >
            Generate report
          </Button>
        </Filters.MobileButtonsRow>
      </Filters.Buttons>
    );
  };

  const onDateRangeFilterChange = (dateStart, dateEnd) => {
    setDateFilter({
      from: dateStart,
      to: dateEnd,
    });
  };

  const onFilterClick = (e, filterColumn) => {
    if (e.target.checked) {
      setActiveFilters([...activeFilters, e.target.name]);
    } else {
      setActiveFilters((prevFilters: string[]) => {
        const updatedFilters = prevFilters.filter((filter) => filter !== e.target.name);

        const filtersToRemove = filterColumn.filters
          .filter(
            (filter: { name: string; require?: string[] }) =>
              filter.name !== e.target.name &&
              filter.require &&
              !filter.require.some((req) => updatedFilters.includes(req))
          )
          .map((filter) => filter.name);

        return updatedFilters.filter((filter) => !filtersToRemove.includes(filter));
      });
    }
  };

  const filterDisabled = (columnName) => {
    return (
      (!hasSkillDive && columnName === skillDiveColumnName) ||
      (!sonarEnabled && columnName === skillSonarColumnName)
    );
  };

  const isUsersFiltersGroup = (filter) => {
    return filter.name === usersColumnName;
  };
  const isCoursesFiltersGroup = (filter) => {
    return filter.name === coursesColumnName;
  };
  const isTeamFiltersGroup = (filter) => {
    return filter.name === teamColumnName;
  };

  const filterColumns = (filters) => {
    const checkBoxesLists = [];
    let filtersColumn = [];
    const columnName = filters.name;
    const filtersList = filters.filters;

    filtersList.forEach((filter) => {
      if (filter.showTo.includes(filtersType)) {
        let filtersPerColumn;
        switch (filtersType) {
          case USER_ACTIVITY:
            filtersPerColumn = isUsersFiltersGroup(filters) ? 3 : 7;
            break;
          case TEAM_ACTIVITY:
            filtersPerColumn = isTeamFiltersGroup(filters) ? 3 : 7;
            break;
          case COURSES:
            filtersPerColumn = isCoursesFiltersGroup(filters) ? 3 : 7;
            break;
          default:
            filtersPerColumn = 4;
            break;
        }

        if (
          !userProgressReportsCheck &&
          ['users_with_not_progress', 'teams_with_not_progress'].includes(filter.name)
        ) {
          return;
        }

        const isGreyed = filterDisabled(columnName);
        const isDisabled = filter.require?.every((req) => !activeFilters.includes(req));

        const toggleFromContainer = () => {
          if (isGreyed || isDisabled) return;
          const willCheck = !activeFilters.includes(filter.name);
          onFilterClick({ target: { name: filter.name, checked: willCheck } }, filters);
        };

        const filterElem = (
          <Filter.Item
            key={filter.name}
            greyed={isGreyed}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if ((e.target as HTMLElement)?.closest?.('.ant-checkbox-wrapper')) return;
              toggleFromContainer();
            }}
            onKeyDown={(e) => {
              if ((e.target as HTMLElement)?.closest?.('.ant-checkbox-wrapper')) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFromContainer();
              }
            }}
          >
            <Filter.Checkbox
              key={filter.name}
              onChange={(e) => onFilterClick(e, filters)}
              checked={activeFilters.includes(filter.name)}
              controlled
              disabled={isDisabled}
              name={filter.name}
              id={filter.name}
              label={filter.label}
              labelColor={fetchingFilters ? 'var(--grey-dark)' : undefined}
            />
            {filter.tooltip && filter.tooltip[filtersType] && (
              <Tooltip
                title={filter.tooltip[filtersType]}
                placement="top"
                trigger={isMobile ? ['click'] : ['hover']}
              >
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.currentTarget as HTMLSpanElement).click();
                    }
                  }}
                >
                  <FeatherIcon icon="info" size={14} color="#A793F6" />
                </span>
              </Tooltip>
            )}
          </Filter.Item>
        );

        if (filtersColumn.length === filtersPerColumn) {
          filtersColumn = [];
          filtersColumn.push(filterElem);
        } else {
          filtersColumn.push(filterElem);
        }
      }
      if (!checkBoxesLists.includes(filtersColumn)) checkBoxesLists.push(filtersColumn);
    });

    return checkBoxesLists.map((filterList, i) => (
      <Filter.List key={`filter-group-${++i}`}>{filterList}</Filter.List>
    ));
  };

  return (
    <Filters.Container>
      {/* Action buttons removed; use the top ones near the metrics counter */}
      <Filters.AdvancedViewHeader>
        <DateRangeFilter setDates={onDateRangeFilterChange} />
        {filtersType === USER_ACTIVITY && (
          <Filters.IdsFilterContainer>
            <UsersSelectMultiple onUserChange={handleUsersSelectionChange} />
          </Filters.IdsFilterContainer>
        )}
        {filtersType === TEAM_ACTIVITY && (
          <Filters.TeamFilterContainer>
            <>Filter by teams</>
            <TeamsSelector
              analyticsTheme
              teamsSelected={teamsSelected}
              onTeamChange={handleTeamsSelectionChange}
              selectAllOption={isTeamManager ? 'All my teams' : 'All teams from the account'}
              containerStyles={{ minWidth: '70%' }}
            />
          </Filters.TeamFilterContainer>
        )}
      </Filters.AdvancedViewHeader>
      <Filters.AdvancedView aria-busy={fetchingFilters}>
        {fetchingFilters && (
          <Filters.LoadingOverlay>
            <Spinner active top="50%" left="50%" />
          </Filters.LoadingOverlay>
        )}
        {filters().map((filter) => {
          const coursesFiltersGroup = isCoursesFiltersGroup(filter);
          const isSkillDiveLicenseFiltersGroup = filter.name === 'Skill Dive Licenses';
          const isEndOfRow =
            (filtersType === COURSES && coursesFiltersGroup) ||
            (filtersType === USER_ACTIVITY && isUsersFiltersGroup(filter)) ||
            (filtersType === TEAM_ACTIVITY && isTeamFiltersGroup(filter)) ||
            (filtersType === ACCOUNT && isSkillDiveLicenseFiltersGroup);
          const displayContainerTitle =
            ((filtersType === USER_ACTIVITY || filtersType === TEAM_ACTIVITY) &&
              filter.name === skillDiveColumnName) ||
            (filtersType === ACCOUNT && filter.name === 'Overall usage');
          const filterElements = filterColumns(filter);
          const columnTooltip = filterDisabled(filter.name)
            ? filter.columnDisabledTooltip
            : filter.tooltip;
          return (
            filter.showTo.includes(filtersType) && (
              <React.Fragment key={filter.name}>
                {displayContainerTitle && (
                  <AdvanceSubtitle>Content progress & Usage</AdvanceSubtitle>
                )}
                <Filter.Container key={filter.name} filtersType={filtersType} endOfRow={isEndOfRow}>
                  <Filter.Title>
                    {filter.name}
                    {columnTooltip && (
                      <Tooltip title={columnTooltip} placement="right">
                        <FeatherIcon icon="info" size={14} color="#A793F6" />
                      </Tooltip>
                    )}
                  </Filter.Title>
                  <Filter.Group twoColumns={filterElements?.length > 1}>
                    {filterElements}
                  </Filter.Group>
                </Filter.Container>
              </React.Fragment>
            )
          );
        })}
      </Filters.AdvancedView>
      <ActionButtons />
      {showScheduleReportModal && (
        <ScheduleReportModal
          filtersType={filtersType}
          closeModal={() => setShowScheduleReportModal(false)}
        />
      )}
      {/* Hidden test hook: keeps tests working without rendering duplicate action buttons */}
      <button
        data-testid="filter-button-test-id"
        style={{ display: 'none' }}
        onClick={() => onGenerateReportClick()}
        aria-hidden="true"
        tabIndex={-1}
      />
    </Filters.Container>
  );
};

export default ReportingFilters;
